import { FhirInstance } from '../config/FhirClient';
import { IBundle } from '../modules/unsw-fhir/Bundle';
import {
  IOperationOutcome,
  isOperationOutcome,
} from '../modules/unsw-fhir/OperationOutcome';
import { IPatient, isPatient } from '../modules/unsw-fhir/Patient';
import { getNextLinkStr, getSelfLinkStr } from '../selectors/bundle';

type ResponsePayload = {
  next?: string;
  data: IPatient[];
  previous?: string;
};

export const getPatientSummaries = async (
  count: string,
  link?: string,
  filtered?: { id: string; value: string }[],
  active: boolean = true,
  organizationId?: string,
): Promise<ResponsePayload> => {
  const path = link ? link : '/Patient';
  const params: {
    [key: string]: string | number | boolean;
  } = { _sort: 'family' };
  if (!link) params._count = 20;
  if (!!filtered)
    for (const value of filtered) {
      params[value.id] = value.value;
    }
  if (active) params.active = active;

  if (organizationId) params.organization = organizationId;
  try {
    const response = await FhirInstance.get<IBundle | IOperationOutcome>(path, {
      params,
    });
    const { data } = response;
    if (isOperationOutcome(data) || !data.entry) {
      return { data: [] };
    }

    const retV: ResponsePayload = {
      previous: getSelfLinkStr(data),
      data: [],
      next: getNextLinkStr(data),
    };

    retV.data = data.entry
      .filter(entry => {
        return !!entry.resource && isPatient(entry.resource);
      })
      .map((entry): IPatient => entry.resource as IPatient);

    return retV;
  } catch (e) {
    console.error(e);
    return { data: [] };
  }
};

export const postPatient = async (patient: IPatient) => {
  try {
    const response = await FhirInstance.post<IPatient | IOperationOutcome>(
      '/Patient',
      patient,
    );
    const { data } = response;
    if (isOperationOutcome(data)) {
      return undefined;
    }
    return data;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const putPatient = async (id: string, patient: IPatient) => {
  try {
    const response = await FhirInstance.put<IPatient | IOperationOutcome>(
      `/Patient/${id}`,
      patient,
    );
    const { data } = response;
    if (isOperationOutcome(data)) {
      return undefined;
    }
    return data;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const getPatientById = async (id: string) => {
  try {
    const response = await FhirInstance.get<IPatient | IOperationOutcome>(
      `/Patient/${id}`,
    );
    const { data } = response;
    if (isOperationOutcome(data)) {
      return undefined;
    }
    return data;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const archivePatient = async (id: string) => {
  try {
    const response = await FhirInstance.patch<IPatient | IOperationOutcome>(
      `/Patient/${id}`,
      { active: false },
    );
    const { data } = response;
    if (isOperationOutcome(data)) {
      return undefined;
    }
    return data;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};
export const deletePatient = async (id: string) => {
  try {
    const response = await FhirInstance.delete<IPatient | IOperationOutcome>(
      `/Patient/${id}`,
    );
    const { data } = response;
    if (isOperationOutcome(data)) {
      return undefined;
    }
    return data;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const getNumPatients = async () => {
  try {
    const response = await FhirInstance.get<IBundle | IOperationOutcome>(
      '/Patient?_summary=count',
    );
    const { data } = response;
    if (isOperationOutcome(data) || !data.entry) {
      throw new Error('unable to get patient count');
    }

    return data.total;
  } catch (e) {
    console.error(e);
  }
};

export const getAllPatients = async () => {
  const count = await getNumPatients();
  return await getPatients(count);
};

export const getPatients = async (count?: number) => {
  try {
    let queryStr = '';
    if (count) {
      const params = { _count: count.toString(10) };
      queryStr = new URLSearchParams(params).toString();
    }

    const response = await FhirInstance.get<IBundle | IOperationOutcome>(
      '/Patient' + queryStr,
    );
    const { data } = response;
    if (isOperationOutcome(data) || !data.entry) {
      return [];
    }

    return data.entry
      .filter(entry => {
        return !!entry.resource && isPatient(entry.resource);
      })
      .map((entry): IPatient => entry.resource as IPatient);
  } catch (e) {
    console.error(e);
    return [];
  }
};
